import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { cleanContentsForSave } from '../../src/Pages/Iximaker/Action/tmpIdUtils'; // Ajustez le chemin selon votre structure
import { getDefaultHologramState, normalizeHologramStatesInJson } from '../utils/hologramState';

// Topic pré-rempli à la création d'un learn : calibrage de la zone de travail
const getCalibrationTopic = () => ({
    topicID: 1,
    topicTitle: "Calibrage de la zone de travail",
    topicDesc: "Pincez la zone de travail pour la déplacer à l'endroit désiré.\nUtilisez la poignée rouge pour l'orienter.",
    topicImg: "",
    topicHologram: "",
    linkedStageID: "0",
    topicEnv360: "",
    topicEnv360Rot: "",
    topicHologramOffsetPos: { x: 0, y: 0, z: 0 },
    topicHologramOffsetRot: { x: 0, y: 0, z: 0 },
    topicHologramInteractable: false,
    topicHologramState: getDefaultHologramState(),
    topicAudioText: "Pincez la zone de travail pour la déplacer à l'endroit désiré.\nUtilisez la poignée rouge pour l'orienter.",
    topicAudio: "",
    quizzes: []
});

// Thunk asynchrone pour fetch le JSON depuis le back
// Dans votre jsonSliceLearn.js, remplacez le thunk fetchJsonFromBack par :

export const fetchJsonFromBack = createAsyncThunk(
    'jsonLearn/fetchJsonFromBack',
    async ({ folderName, filename }, { rejectWithValue }) => {
        try {
            const cleanFolderName = folderName ? folderName.replace(/\.json$/, '') : '';
            const cleanFilename = filename ? filename.replace(/\.json$/, '') : '';
            console.log(filename + ' / ' + cleanFilename);

            if (!cleanFolderName) {
                throw new Error('Le nom du dossier (folderName) est vide ou non défini. Impossible de charger le JSON.');
            }
            if (!cleanFilename) {
                throw new Error('Le nom du fichier (filename) est vide ou non défini. Impossible de charger le JSON.');
            }

            const url = `/api/data/${cleanFolderName}/${cleanFilename}.json`;
            console.log('🔍 FETCH URL:', url);

            const response = await axios.get(url, { withCredentials: true });

            console.log('🔍 FETCH RESPONSE:', response);
            console.log('🔍 FETCH RESPONSE DATA:', response.data);

            // CORRECTION : Utiliser response directement si response.data est undefined
            const data = response.data !== undefined ? response.data : response;
            console.log('🔍 FINAL DATA:', data);

            if (!data || typeof data !== 'object') {
                throw new Error('Réponse vide ou invalide du serveur');
            }

            return data;
        } catch (error) {
            console.error('❌ FETCH ERROR:', error);
            console.error('❌ FETCH ERROR MESSAGE:', error.message);
            console.error('❌ FETCH ERROR RESPONSE:', error.response);

            return rejectWithValue(error.response?.data?.error || error.message || 'Erreur lors du chargement des données');
        }
    }
);

// Thunk asynchrone pour sauvegarder les données vers le backend
export const saveJsonLearnToBack = createAsyncThunk(
    'jsonLearn/saveJsonToBack',
    async (payload, { getState }) => {
        // payload: { updatedData, chapterName, languageFolder, filename }
        const { updatedData, chapterName, languageFolder, filename } = payload || {};
        try {
            // Construction stricte du JSON Learn
            const state = getState();
            const existingData = state.jsonLearn.jsonData || {};
            const dataToSave = {
                usage: updatedData.usage || existingData.usage || "Fichier des questionnaires liés aux procédures (process) d'iXiLEARN.",
                settings: updatedData.settings || existingData.settings || {},
                process: {
                    topics: Array.isArray(updatedData.process?.topics) ? updatedData.process.topics : (existingData.process?.topics || []),
                    courses: Array.isArray(updatedData.process?.courses) ? updatedData.process.courses : (existingData.process?.courses || [])
                }
            };

            // ✅ NETTOYER LES _tmpId AVANT SAUVEGARDE
            // Les sous-objets <préfixe>HologramState sont toujours écrits (topics et questions)
            const cleanedDataToSave = normalizeHologramStatesInJson(cleanContentsForSave(dataToSave));

            if (!chapterName || !languageFolder || !filename) throw new Error('Le chapitre, le dossier langue ou le nom du fichier ne sont pas définis');
            const cleanChapter = chapterName.replace(/\.json$/, '');
            const cleanLanguageFolder = languageFolder.replace(/\.json$/, '');
            const cleanFilename = filename.replace(/\.json$/, '');
            const completePath = `/api/data/${cleanChapter}/${cleanLanguageFolder}/${cleanFilename}`;

            // ✅ ENVOYER LES DONNÉES NETTOYÉES
            const response = await axios.put(completePath, cleanedDataToSave, { withCredentials: true });

            return response.data || cleanedDataToSave;
        } catch (error) {
            throw error;
        }
    }
);

const initialState = {
    jsonData: {
        usage: "Fichier des questionnaires liés aux procédures (process) d'iXiLEARN.",
        settings: {
            contentVersion: "",
            ownerName: "",
            ownerIdentifier: "",
            appVersion: "",
            referentiel: "",
            variant: "",
            unit: "mm",
            langCode: "FR"
        },
        process: {
            topics: [],
            courses: []
        }
    },
    loading: false,
    error: null,
    folderName: null,
    filename: null
};

const jsonSliceLearn = createSlice({
    name: 'jsonLearn',
    initialState,
    reducers: {
        initializeJson: (state) => {
            state.jsonData = {
                usage: "Fichier des questionnaires liés aux procédures (process) d'iXiLEARN.",
                settings: {
                    contentVersion: "1.0",
                    ownerName: "iXiVERSE",
                    ownerIdentifier: "00000000000000",
                    appVersion: "",
                    chapter: "",
                    calibrateAfterStageID: null,
                    unit: "mm",
                    langCode: "FR"
                },
                process: {
                    topics: [],
                    courses: []
                }
            };
        },
        initializeJsonFromFile: (state, action) => {
            state.jsonData = action.payload;
        },
        updateJsonDataLearn: (state, action) => {
            state.jsonData = action.payload;
        },
        updateSettingsField: (state, action) => {
            if (!state.jsonData.settings) state.jsonData.settings = {};
            state.jsonData.settings[action.payload.field] = action.payload.value;
        },
        addOrUpdateCourse: (state, action) => {
            const { courseID, data } = action.payload;
            if (!state.jsonData.process.courses) state.jsonData.process.courses = [];
            const idx = state.jsonData.process.courses.findIndex(c => c.courseID === courseID);
            if (idx !== -1) state.jsonData.process.courses[idx] = data;
            else state.jsonData.process.courses.push(data);
        },
        addOrUpdateTopic: (state, action) => {
            const { topicID, data } = action.payload;
            if (!state.jsonData.process.topics) state.jsonData.process.topics = [];
            const idx = state.jsonData.process.topics.findIndex(t => t.topicID === topicID);
            if (idx !== -1) state.jsonData.process.topics[idx] = data;
            else state.jsonData.process.topics.push(data);
        },
        addOrUpdateQuiz: (state, action) => {
            const { topicID, quizIndex, data } = action.payload;
            const topic = state.jsonData.process.topics.find(t => t.topicID === topicID);
            if (!topic) return;
            if (!topic.quizzes) topic.quizzes = [];
            if (quizIndex !== undefined && topic.quizzes[quizIndex]) {
                topic.quizzes[quizIndex] = data;
            } else {
                topic.quizzes.push(data);
            }
        },
        addOrUpdateQuizChoice: (state, action) => {
            const { topicID, quizIndex, choiceIndex, data } = action.payload;
            const topic = state.jsonData.process.topics.find(t => t.topicID === topicID);
            if (!topic) return;
            const quiz = topic.quizzes[quizIndex];
            if (!quiz) return;
            if (!quiz.quizChoices) quiz.quizChoices = [];
            if (choiceIndex !== undefined && quiz.quizChoices[choiceIndex]) {
                quiz.quizChoices[choiceIndex] = data;
            } else {
                quiz.quizChoices.push(data);
            }
        },
    },
    // Dans votre jsonSliceLearn.js, remplacez la section extraReducers par :

    extraReducers: (builder) => {
        builder
            .addCase(fetchJsonFromBack.pending, (state) => {
                state.loading = true;
                state.error = null; // 👈 Réinitialiser l'erreur
            })
            .addCase(fetchJsonFromBack.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null; // 👈 Réinitialiser l'erreur

                console.log('🔍 DEBUG fetchJsonFromBack.fulfilled:', {
                    actionPayload: action.payload,
                    payloadType: typeof action.payload,
                    payloadKeys: action.payload ? Object.keys(action.payload) : 'null'
                });

                // Test plus permissif - vérifier si c'est un objet avec des propriétés
                if (!action.payload || typeof action.payload !== 'object') {
                    state.error = "Aucune donnée reçue du serveur";
                    console.error('❌ Payload invalide:', action.payload);
                    return;
                }

                state.jsonData = action.payload;

                console.log('✅ jsonData mis à jour:', state.jsonData);
            })
            .addCase(fetchJsonFromBack.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error?.message || "Erreur de chargement";
                state.jsonData = {
                    usage: "Fichier des questionnaires liés aux procédures (process) d'iXiLEARN.",
                    settings: {
                        contentVersion: "",
                        ownerName: "",
                        ownerIdentifier: "",
                        appVersion: "",
                        chapter: "",
                        calibrateAfterStageID: null,
                        unit: "mm",
                        langCode: "FR"
                    },
                    process: {
                        topics: [getCalibrationTopic()],
                        courses: []
                    }
                };
                console.error('❌ fetchJsonFromBack rejected:', action.error);
            })
            .addCase(saveJsonLearnToBack.pending, (state) => {
                state.loading = true;
            })
            .addCase(saveJsonLearnToBack.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null; // 👈 Réinitialiser l'erreur après succès
            })
            .addCase(saveJsonLearnToBack.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    }
});

export const {
    initializeJson,
    initializeJsonFromFile,
    updateJsonDataLearn,
    updateSettingsField,
    addOrUpdateCourse,
    addOrUpdateTopic,
    addOrUpdateQuiz,
    addOrUpdateQuizChoice,
} = jsonSliceLearn.actions;
export const initialStateLearn = initialState; // export initial state for export/import buttons
export default jsonSliceLearn.reducer;


//Creators : Florian Cohen-Joly @3DDGAMES -- Under license